import React, { memo, useCallback, useMemo } from 'react'
import SmartWayLogo from 'assets/logos/smartway-logo.jpg'
import { ControlledDatePicker } from 'transfix-form/components'
import ControlledRadioGroupInput, {
  IControlledRadioGroupInputProps,
} from 'transfix-form/components/ControlledRadioGroupInput'
import { FormIf } from 'transfix-form/components/FormIf'
import OpenInNewTabIcon from 'transfix-icons/build/OpenInNewTabIcon'
import SuccessIcon from 'transfix-icons/build/SuccessIcon'
import { Button } from 'transfix-ui/components/Button'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import { Typography, makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { TEST_IDS } from 'apps/fleet-planner/pages/carrier-profile/constants'
import { TSmartWayCarrierSubmitHandler } from '../../types'
import {
  IUseSmartWayCarrierFormProps,
  useSmartWayCarrierForm,
} from './hooks/useSmartWayCarrierForm'
import { getErrorMessage, TSmartWayCarrierSchema } from './schema/SmartWayCarrierSchema'

export interface ISmartWayCarrierModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseSmartWayCarrierFormProps['defaultValues']
  loading: boolean
  onSubmit: TSmartWayCarrierSubmitHandler<TSmartWayCarrierSchema>
}

const SMARTWAY_CARRIER_OPTIONS: IControlledRadioGroupInputProps['options'] = [
  { label: 'Yes', id: true },
  { label: 'No', id: false },
]

const useSmartWayCarrierModalStyles = makeStyles(theme => ({
  smartwayLogo: {
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  marginLeft: {
    marginLeft: theme.spacing(2),
  },
  marginBottom: {
    marginBottom: theme.spacing(2),
  },
  smartWayRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  smartWayColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

const SmartWayCarrierModal = memo<ISmartWayCarrierModalProps>(
  ({ defaultValues = {}, loading, onClose, onSubmit }) => {
    const classes = useSmartWayCarrierModalStyles()
    const { control, errors, handleSubmit } = useSmartWayCarrierForm({ defaultValues })
    const smartWayCarrierErrors = getErrorMessage(errors, 'renewalDate')

    const submitHandler = useCallback(
      async (formValues: TSmartWayCarrierSchema) => {
        const result = await onSubmit(formValues)
        if (result) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const actions: IFormModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          disabled: loading,
        },
        {
          label: 'Update',
          type: 'confirm',
          action: handleSubmit(submitHandler),
          disabled: loading,
        },
      ],
      [handleSubmit, loading, onClose, submitHandler]
    )

    return (
      <FormModal title='SmartWay Carrier' actions={actions} size={5} onClose={onClose} open>
        <Grid container>
          <Grid item xs={3}>
            <img src={SmartWayLogo} alt='' className={classes.smartwayLogo} />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.marginBottom}>
              <Typography component='div'>
                Becoming a Smartway Carrier Partner helps you:
              </Typography>
            </div>
            <div className={classes.smartWayRow}>
              <div>
                <SuccessIcon size='xlarge' color='green2' />
              </div>
              <div className={`${classes.smartWayColumn} ${classes.marginLeft}`}>
                <Typography variant='subtitle1'>Track & Account for Emissions</Typography>
                <Typography variant='body2'>
                  Get credible efficiency tracking and emissions accounting allowing you to
                  benchmark against industry standards.
                </Typography>
              </div>
            </div>
            <div className={`${classes.smartWayRow} ${classes.marginTop}`}>
              <div>
                <SuccessIcon size='xlarge' color='green2' />
              </div>
              <div className={`${classes.smartWayColumn} ${classes.marginLeft}`}>
                <Typography variant='subtitle1'>Differentiate from the Competition</Typography>
                <Typography variant='body2'>
                  Showcase your commitment to sustainability and differentiate your company when
                  partnering with environmentally-conscious shippers.
                </Typography>
              </div>
            </div>
            <div className={`${classes.smartWayRow} ${classes.marginTop}`}>
              <div>
                <SuccessIcon size='xlarge' color='green2' />
              </div>
              <div className={`${classes.smartWayColumn} ${classes.marginLeft}`}>
                <Typography variant='subtitle1'>Reduce Freight Waste</Typography>
                <Typography variant='body2'>
                  Make strategic improvements year after year to increase profitability and lead the
                  transition to greener freight.
                </Typography>
              </div>
            </div>
          </Grid>
          <div className={`${classes.smartWayRow} ${classes.marginTop}`}>
            <Button
              startIcon={<OpenInNewTabIcon size='large' />}
              href='https://www.epa.gov/smartway/become-smartway-carrier-partner'
              target='_blank'>
              Learn More
            </Button>
          </div>
          <Grid item xs={12} className={classes.marginTop}>
            <ControlledRadioGroupInput
              control={control}
              name='isRegistered'
              options={SMARTWAY_CARRIER_OPTIONS}
              stacked={false}
              RadioProps={{
                dataTest: TEST_IDS.smartWayCarrierModal,
              }}
            />
            <FormIf control={control} name='isRegistered' when={(val: boolean) => val === true}>
              <div className={classes.marginTop}>
                <Box maxWidth={200}>
                  <ControlledDatePicker
                    control={control}
                    name='renewalDate'
                    defaultValue={null}
                    DatePickerProps={{
                      dataTest: 'smart-way-carrier-renewal-date',
                      placeholder: 'Renewal Date',
                      label: 'Renewal Date',
                      invalid: !!smartWayCarrierErrors,
                      validationMessage: smartWayCarrierErrors,
                      minDate: Date.now(),
                    }}
                  />
                </Box>
              </div>
            </FormIf>
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default SmartWayCarrierModal
